// The Background fields the basic background editors don't cover.
//
// Both basic editors (blockEditors.tsx `BackgroundEditor`, used by hero /
// cta_banner / container / logo_cloud, and BlockStyleEditor.tsx's own one,
// used by the per-block Style panel) edit kind + colour + gradient from/to/
// angle + image URL + parallax. The schema carries more than that — video
// backgrounds, multi-stop and radial gradients, a gradient wash over an image
// — and a page saved with any of those showed nothing editable for it.
//
// This component renders ONLY those extra fields, for the kind the value
// already has, so a host mounts it under its own controls and nothing is
// duplicated. Every change spreads the incoming value, so sibling fields the
// host owns are never dropped.
//
// Runtime reference (keep in step): packages/page-runtime/src/theme.ts
// `gradientValue` / `backgroundCss`, renderers.tsx `backgroundStyle` /
// `VideoBackdrop` / `backgroundClass`.

import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react'
import type { Background, BackgroundKind, GradientStop } from './types'
import ColorValueRow from './ColorValueRow'
import { AssetPickerField } from './AssetPicker'

// ─── Kind options ────────────────────────────────────────

/**
 * EVERY `BackgroundKind` in the schema, in menu order, with its label. A host
 * kind <select> renders these (plus its own "unset" entry if it has one) so a
 * saved `video` / `gradient_animated` never displays as a different kind.
 */
export const BACKGROUND_KIND_OPTIONS: ReadonlyArray<{ value: BackgroundKind; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'transparent', label: 'Transparent' },
  { value: 'color', label: 'Solid color' },
  { value: 'gradient', label: 'Gradient' },
  { value: 'gradient_animated', label: 'Animated gradient' },
  { value: 'image', label: 'Image URL' },
  { value: 'video', label: 'Video' },
]

/** True when `kind` is one the schema knows (else show it as unrecognised). */
export function isKnownBackgroundKind(kind: string | undefined): boolean {
  return !!kind && BACKGROUND_KIND_OPTIONS.some((o) => o.value === kind)
}

/**
 * Build the background for a kind switch, carrying over the fields that mean
 * something for the target kind (so Image → Video → Image keeps the overlay,
 * and Gradient ↔ Animated gradient keeps its stops) and dropping the rest.
 */
export function backgroundForKind(prev: Background | undefined, kind: BackgroundKind): Background {
  const p: Partial<Background> = prev || {}
  switch (kind) {
    case 'color':
      return { kind, color: p.color || '#ffffff' }
    case 'gradient':
    case 'gradient_animated':
      return pruneUndefined({
        kind,
        gradientFrom: p.gradientFrom || '#0f172a',
        gradientTo: p.gradientTo || '#1e3a8a',
        gradientAngle: p.gradientAngle ?? 135,
        stops: p.stops,
        gradientType: p.gradientType,
        gradientShape: p.gradientShape,
      })
    case 'image':
      return pruneUndefined({
        kind,
        imageUrl: p.imageUrl || '',
        overlay: p.overlay,
        overlayGradient: p.overlayGradient,
        parallax: p.parallax,
        parallaxIntensity: p.parallaxIntensity,
      })
    case 'video':
      return pruneUndefined({
        kind,
        videoUrl: p.videoUrl || '',
        // A still image is the natural poster when coming from Image.
        posterUrl: p.posterUrl || p.imageUrl,
        color: p.color,
      })
    default:
      return { kind }
  }
}

// ─── Component ───────────────────────────────────────────

/**
 * What the surface the host edits actually paints. The block-band renderers
 * (hero / CTA banner) honour all of it; the Style-panel path
 * (`theme.ts backgroundCss`) paints a video's poster only, a still gradient
 * for the animated kind, and no gradient overlay. Each defaults to `true`;
 * `false` adds an honest hint (and, for the overlay gradient, shows it only
 * when a saved value already carries one, so it can be seen and removed).
 */
export interface BackgroundExtrasSupport {
  videoLayer?: boolean
  drift?: boolean
  overlayGradient?: boolean
}

export interface BackgroundExtrasEditorProps {
  value: Background
  project: string
  onChange: (b: Background) => void
  /**
   * Render the flat `overlay` colour for kind=image. Default `true`; pass
   * `false` when the host already has its own overlay input.
   */
  showOverlay?: boolean
  support?: BackgroundExtrasSupport
}

export default function BackgroundExtrasEditor({
  value, project, onChange, showOverlay = true, support,
}: BackgroundExtrasEditorProps) {
  const set = (next: Partial<Background>) => onChange(pruneUndefined({ ...value, ...next }))

  if (value.kind === 'gradient' || value.kind === 'gradient_animated') {
    return <GradientExtras value={value} set={set} drift={support?.drift !== false} />
  }
  if (value.kind === 'video') {
    return <VideoExtras value={value} project={project} set={set} videoLayer={support?.videoLayer !== false} />
  }
  if (value.kind === 'image') {
    return (
      <ImageExtras
        value={value}
        set={set}
        showOverlay={showOverlay}
        overlayGradientSupported={support?.overlayGradient !== false}
      />
    )
  }
  return null
}

export { BackgroundExtrasEditor }

// ─── Gradient: type / radial center / colour stops ───────

const RADIAL_CENTERS: ReadonlyArray<{ value: NonNullable<Background['gradientShape']>; label: string }> = [
  { value: 'center', label: 'Center' },
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'top-left', label: 'Top left' },
  { value: 'top-right', label: 'Top right' },
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'bottom-right', label: 'Bottom right' },
]

function GradientExtras({ value, set, drift }: {
  value: Background
  set: (next: Partial<Background>) => void
  drift: boolean
}) {
  const radial = value.gradientType === 'radial'
  return (
    <div style={GROUP}>
      <div style={ROW}>
        <div style={{ flex: 1 }}>
          <label style={LABEL}>Type</label>
          <select
            value={value.gradientType || ''}
            onChange={(e) => set({
              gradientType: (e.target.value || undefined) as Background['gradientType'],
            })}
            style={INPUT}
          >
            <option value="">Linear (default)</option>
            <option value="linear">Linear</option>
            <option value="radial">Radial</option>
            {value.gradientType && !['linear', 'radial'].includes(value.gradientType) && (
              <option value={value.gradientType}>{value.gradientType} (unrecognised)</option>
            )}
          </select>
        </div>
        {(radial || value.gradientShape) && (
          <div style={{ flex: 1 }}>
            <label style={LABEL}>Radial center</label>
            <select
              value={value.gradientShape || ''}
              onChange={(e) => set({
                gradientShape: (e.target.value || undefined) as Background['gradientShape'],
              })}
              style={INPUT}
            >
              <option value="">Center (default)</option>
              {RADIAL_CENTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              {value.gradientShape && !RADIAL_CENTERS.some((o) => o.value === value.gradientShape) && (
                <option value={value.gradientShape}>{value.gradientShape} (unrecognised)</option>
              )}
            </select>
          </div>
        )}
      </div>
      {radial && <p style={HINT}>A radial gradient ignores the angle.</p>}
      {!radial && value.gradientShape && (
        <p style={HINT}>The center only applies to a radial gradient.</p>
      )}

      <StopsEditor
        label="Colour stops"
        stops={value.stops}
        seed={[value.gradientFrom || '#0f172a', value.gradientTo || '#1e3a8a']}
        replacesNote="From / To"
        onChange={(stops) => set({ stops })}
      />

      {value.kind === 'gradient_animated' && !drift && (
        <p style={HINT}>
          This surface paints the gradient still; the slow drift plays on a
          block's own background (hero, CTA banner).
        </p>
      )}
    </div>
  )
}

// ─── Video: source + poster + fallback colour ────────────

function VideoExtras({ value, project, set, videoLayer }: {
  value: Background
  project: string
  set: (next: Partial<Background>) => void
  videoLayer: boolean
}) {
  return (
    <div style={GROUP}>
      <div>
        <label style={LABEL}>Video</label>
        <AssetPickerField
          project={project}
          value={value.videoUrl}
          accept="video/*"
          onChange={(url) => set({ videoUrl: url || undefined })}
        />
      </div>
      <div>
        <label style={LABEL}>Poster frame</label>
        <AssetPickerField
          project={project}
          value={value.posterUrl}
          accept="image/*"
          onChange={(url) => set({ posterUrl: url || undefined })}
        />
        <p style={{ ...HINT, marginTop: 4 }}>
          Required in practice: the poster is all a static page, a visitor who
          asked for reduced motion, or a slow connection ever sees.
          {!value.posterUrl && ' Without one the band shows the fallback colour.'}
        </p>
      </div>
      <div>
        <label style={LABEL}>Fallback colour (no poster)</label>
        <ColorValueRow
          value={value.color || ''}
          placeholder="var(--pg-surface)"
          onChange={(c) => set({ color: c || undefined })}
        />
      </div>
      {!videoLayer && (
        <p style={HINT}>
          This surface paints the poster frame only; the moving video plays on
          a block's own background (hero, CTA banner).
        </p>
      )}
    </div>
  )
}

// ─── Image: overlay colour + overlay gradient ────────────

function ImageExtras({ value, set, showOverlay, overlayGradientSupported }: {
  value: Background
  set: (next: Partial<Background>) => void
  showOverlay: boolean
  overlayGradientSupported: boolean
}) {
  const og = value.overlayGradient
  const showOg = overlayGradientSupported || !!og
  if (!showOverlay && !showOg) return null
  return (
    <div style={GROUP}>
      {showOverlay && (
        <div>
          <label style={LABEL}>Overlay colour</label>
          <ColorValueRow
            value={value.overlay || ''}
            placeholder="none — e.g. rgba(0,0,0,0.4)"
            onChange={(c) => set({ overlay: c || undefined })}
          />
        </div>
      )}
      {showOg && (
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!og}
              onChange={(e) => set({
                overlayGradient: e.target.checked
                  ? { from: 'rgba(0,0,0,0.6)', to: 'rgba(0,0,0,0)', angle: 180 }
                  : undefined,
              })}
            />
            Gradient overlay
          </label>
          {!overlayGradientSupported && og && (
            <p style={{ ...HINT, marginTop: 4 }}>
              This surface doesn't paint a gradient overlay — use the overlay
              colour, or untick to remove it.
            </p>
          )}
          {og && (
            <div style={{ ...GROUP, marginTop: 6 }}>
              {value.overlay && (
                <p style={HINT}>The gradient wins over the flat overlay colour when both are set.</p>
              )}
              <div style={ROW}>
                <div style={{ flex: 1 }}>
                  <label style={LABEL}>From</label>
                  <ColorValueRow
                    value={og.from || ''}
                    placeholder="rgba(0,0,0,0.6)"
                    onChange={(c) => set({ overlayGradient: { ...og, from: c } })}
                  />
                </div>
              </div>
              <div style={ROW}>
                <div style={{ flex: 1 }}>
                  <label style={LABEL}>To</label>
                  <ColorValueRow
                    value={og.to || ''}
                    placeholder="rgba(0,0,0,0)"
                    onChange={(c) => set({ overlayGradient: { ...og, to: c } })}
                  />
                </div>
              </div>
              <div>
                <label style={LABEL}>Angle (deg)</label>
                <input
                  type="number" min={0} max={360}
                  value={og.angle ?? ''}
                  onChange={(e) => set({
                    overlayGradient: pruneUndefined({ ...og, angle: numOrUndef(e.target.value) }),
                  })}
                  placeholder="180 (top → bottom)"
                  style={INPUT}
                />
              </div>
              <StopsEditor
                label="Overlay stops"
                stops={og.stops}
                seed={[og.from || 'rgba(0,0,0,0.6)', og.to || 'rgba(0,0,0,0)']}
                replacesNote="From / To"
                onChange={(stops) => set({ overlayGradient: pruneUndefined({ ...og, stops }) })}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Stops list (shared by the gradient and the overlay) ─

function StopsEditor({ label, stops, seed, replacesNote, onChange }: {
  label: string
  stops: GradientStop[] | undefined
  /** The two-stop shorthand colours, used to seed the first two stops. */
  seed: [string, string]
  replacesNote: string
  onChange: (next: GradientStop[] | undefined) => void
}) {
  const list = Array.isArray(stops) ? stops : []
  const commit = (next: GradientStop[]) => onChange(next.length ? next : undefined)
  const patchAt = (i: number, next: Partial<GradientStop>) =>
    commit(list.map((s, j) => (j === i ? pruneUndefined({ ...s, ...next }) : s)))
  const move = (i: number, d: -1 | 1) => {
    const j = i + d
    if (j < 0 || j >= list.length) return
    const next = list.slice()
    ;[next[i], next[j]] = [next[j], next[i]]
    commit(next)
  }
  const add = () => {
    if (list.length === 0) {
      commit([{ color: seed[0], at: 0 }, { color: seed[1], at: 100 }])
      return
    }
    commit([...list, { color: list[list.length - 1].color || seed[1] }])
  }

  return (
    <div>
      <label style={LABEL}>{label}</label>
      {list.length >= 2 && (
        <p style={{ ...HINT, marginBottom: 6 }}>
          {list.length} stops replace the {replacesNote} colours above, which are ignored.
        </p>
      )}
      {list.length === 1 && (
        <p style={{ ...HINT, marginBottom: 6 }}>
          A single stop is ignored — add another, or the {replacesNote} pair is used.
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {list.map((s, i) => (
          <div key={i} style={ROW}>
            <ColorValueRow
              value={s.color || ''}
              onChange={(c) => patchAt(i, { color: c })}
            />
            <input
              type="number" min={0} max={100}
              value={s.at ?? ''}
              onChange={(e) => patchAt(i, { at: numOrUndef(e.target.value) })}
              placeholder="auto %"
              title="Position (%) — blank spaces stops evenly"
              style={{ ...INPUT, width: 66, flex: 'none' }}
            />
            <IconBtn title="Move up" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={12} /></IconBtn>
            <IconBtn title="Move down" disabled={i === list.length - 1} onClick={() => move(i, 1)}><ArrowDown size={12} /></IconBtn>
            <IconBtn title="Remove stop" onClick={() => commit(list.filter((_, j) => j !== i))}><X size={12} /></IconBtn>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="btn btn-ghost btn-sm"
        style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <Plus size={12} /> {list.length === 0 ? 'Use colour stops' : 'Add stop'}
      </button>
    </div>
  )
}

function IconBtn({ title, disabled, onClick, children }: {
  title: string; disabled?: boolean; onClick: () => void; children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 24, height: 28, flex: 'none',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--color-bg)', color: 'var(--color-text-secondary)',
        border: '1px solid var(--color-border)', borderRadius: 6,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, padding: 0,
      }}
    >
      {children}
    </button>
  )
}

// ─── Helpers + styles ────────────────────────────────────

/** Drop keys whose value is `undefined`, so persisted JSON stays minimal. */
function pruneUndefined<T extends object>(o: T): T {
  const out = { ...o } as Record<string, unknown>
  for (const k of Object.keys(out)) if (out[k] === undefined) delete out[k]
  return out as T
}

/** Parse a numeric input, treating an empty field as "unset". */
function numOrUndef(v: string): number | undefined {
  if (v === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

const GROUP: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 }

const ROW: React.CSSProperties = { display: 'flex', gap: 6, alignItems: 'center' }

const LABEL: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginBottom: 4,
  display: 'block',
}

const HINT: React.CSSProperties = {
  margin: 0, fontSize: '0.8125rem', lineHeight: 1.45,
  color: 'var(--color-text-secondary)',
}

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.9125rem',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  color: 'var(--color-text)',
  outline: 'none',
}
